import React, { useState, useRef, useEffect } from "react";
import { useParams, useSearchParams, useNavigate, Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { PageLoader, formatDate, formatTime, RATING_FACES } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MapPin, Clock, Users, ShieldCheck, Send, Flag, XCircle, ArrowLeft, Check } from "lucide-react";

const REPORT_CATEGORIES = ["Inappropriate behavior", "Harassment", "Aggressive behavior", "Unwanted contact",
    "False identity", "Safety concern", "Excessive intoxication", "Solicitation", "Repeated no-show", "Other"];

function remainingText(secs) {
    const d = Math.floor(secs / 86400);
    const h = Math.floor((secs % 86400) / 3600);
    const m = Math.floor((secs % 3600) / 60);
    if (d > 0) return `${d} day${d !== 1 ? "s" : ""}, ${h} hour${h !== 1 ? "s" : ""}`;
    if (h > 0) return `${h} hour${h !== 1 ? "s" : ""}, ${m} minute${m !== 1 ? "s" : ""}`;
    return `${m} minute${m !== 1 ? "s" : ""}`;
}

function Chat({ outingId }) {
    const qc = useQueryClient();
    const [text, setText] = useState("");
    const bottomRef = useRef(null);
    const typingRef = useRef(null);
    const { data } = useQuery({
        queryKey: ["chat", outingId],
        queryFn: async () => (await api.get(`/outings/${outingId}/messages`)).data,
        refetchInterval: 3000,
    });
    useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [data?.messages?.length]);
    if (!data) return <PageLoader />;
    const closed = data.status !== "open";
    const presence = data.presence || { arrived: [], typing: [] };
    const meArrived = presence.arrived.some((a) => a.is_me);

    const pingTyping = () => {
        if (typingRef.current) return;
        api.post(`/outings/${outingId}/typing`).catch(() => {});
        typingRef.current = setTimeout(() => { typingRef.current = null; }, 2500);
    };
    const send = async (e) => {
        e.preventDefault();
        if (!text.trim()) return;
        try {
            await api.post(`/outings/${outingId}/messages`, { text });
            setText("");
            qc.invalidateQueries({ queryKey: ["chat", outingId] });
        } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    };
    const toggleArrived = async () => {
        await api.post(`/outings/${outingId}/arrive`, { arrived: !meArrived });
        qc.invalidateQueries({ queryKey: ["chat", outingId] });
    };
    const resv = data.reservation;
    const hoursLeft = resv?.deadline_at ? Math.max(0, Math.round((new Date(resv.deadline_at) - new Date()) / 3600000)) : null;
    const confirmReservation = async () => {
        try {
            await api.post(`/outings/${outingId}/reservation/confirm`);
            toast.success("Reservation confirmed for the group!");
            qc.invalidateQueries({ queryKey: ["chat", outingId] });
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    };

    return (
        <div className="flex h-[62vh] flex-col rounded-2xl bg-card shadow-sm">
            <div className="flex items-center justify-between gap-2 border-b px-4 py-3">
                <p className="text-xs text-muted-foreground">
                    You are <span className="font-700 text-primary">{data.my_identity.temp_emoji} {data.my_identity.temp_name}</span> · logistics only
                </p>
                {!closed && (
                    <Button onClick={toggleArrived} size="sm" variant={meArrived ? "default" : "outline"} data-testid="chat-arrive" className="h-7 rounded-full px-3 text-xs">
                        <MapPin className="mr-1 h-3 w-3" /> {meArrived ? "I'm here" : "I've arrived"}
                    </Button>
                )}
            </div>
            {resv && (resv.status === "booked" ? (
                <div className="border-b border-primary/20 bg-primary/10 px-4 py-2.5" data-testid="chat-reservation-note">
                    <p className="text-[12px] font-700 text-primary">✅ {resv.host_temp_emoji} {resv.host_temp_name} has booked the reservation — you're all set!</p>
                </div>
            ) : (
                <div className="border-b border-accent/25 bg-accent/10 px-4 py-3" data-testid="chat-reservation-note">
                    <p className="text-[12px] leading-snug text-foreground">
                        <span className="font-700 text-accent">📌 Reservation host:</span> {resv.host_temp_emoji} {resv.host_temp_name} was randomly chosen to book this outing and must confirm to the group within <span className="font-700">48 hours</span>{hoursLeft !== null ? ` (about ${hoursLeft}h left)` : ""}.
                    </p>
                    <p className="mt-1 text-[11px] leading-snug text-muted-foreground">
                        If it isn't booked in time, the host's membership is suspended — <span className="font-600">1st &amp; 2nd miss = a warning each, 3rd miss = permanent ban.</span>
                    </p>
                    {resv.is_host && !closed && (
                        <Button size="sm" onClick={confirmReservation} data-testid="reservation-confirm" className="mt-2 rounded-full bg-accent hover:bg-accent/90 text-accent-foreground">
                            I've booked the reservation
                        </Button>
                    )}
                </div>
            ))}
            {presence.arrived.length > 0 && (
                <div className="flex flex-wrap items-center gap-1.5 border-b px-4 py-2" data-testid="chat-arrived-list">
                    <span className="text-[11px] font-600 text-accent">Arrived:</span>
                    {presence.arrived.map((a, i) => (
                        <span key={i} className="rounded-full bg-accent/15 px-2 py-0.5 text-[11px] font-600 text-accent">{a.temp_emoji} {a.temp_name}</span>
                    ))}
                </div>
            )}
            <div className="flex-1 space-y-3 overflow-auto p-4">
                {data.messages.length === 0 && <p className="text-center text-sm text-muted-foreground">Say hello to your group 👋</p>}
                {data.messages.map((m) => (
                    m.system ? (
                        <div key={m.id} className="my-1 flex justify-center" data-testid="chat-system-msg">
                            <span className="max-w-[90%] rounded-2xl bg-secondary px-3 py-1.5 text-center text-[11px] font-600 text-muted-foreground">{m.temp_emoji} {m.text}</span>
                        </div>
                    ) : (
                        <div key={m.id} className={`flex ${m.is_me ? "justify-end" : "justify-start"}`}>
                            <div className={`max-w-[75%] rounded-2xl px-4 py-2 ${m.is_me ? "bg-primary text-primary-foreground" : "bg-secondary text-foreground"}`}>
                                {!m.is_me && <p className="text-[11px] font-700">{m.temp_emoji} {m.temp_name}</p>}
                                <p className="text-sm">{m.text}</p>
                            </div>
                        </div>
                    )
                ))}
                <div ref={bottomRef} />
            </div>
            {presence.typing.length > 0 && (
                <div className="px-4 pb-1 text-xs italic text-muted-foreground" data-testid="chat-typing">
                    {presence.typing.join(", ")} {presence.typing.length > 1 ? "are" : "is"} typing…
                </div>
            )}
            {closed ? (
                <div className="border-t px-4 py-3 text-center text-sm text-muted-foreground">This group chat is closed.</div>
            ) : (
                <form onSubmit={send} className="flex items-center gap-2 border-t p-3">
                    <Input data-testid="chat-input" value={text} onChange={(e) => { setText(e.target.value); pingTyping(); }} placeholder="Message your group…" className="rounded-full" />
                    <Button type="submit" size="icon" data-testid="chat-send" className="rounded-full bg-accent hover:bg-accent/90 text-accent-foreground"><Send className="h-4 w-4" /></Button>
                </form>
            )}
        </div>
    );
}

function FeedbackFlow({ outingId, outingData, onDone }) {
    const qc = useQueryClient();
    const mf = outingData?.my_feedback;
    const win = outingData?.interest_window;
    const locked = win ? !win.open : false;
    const closed = win ? (win.ended && !win.open) : false;
    const [rating, setRating] = useState(mf?.rating || null);
    const [doAgain, setDoAgain] = useState(mf?.do_again ?? null);
    const [attendAnother, setAttendAnother] = useState(mf?.attend_another ?? null);
    const [wantInterest, setWantInterest] = useState((outingData?.my_interested?.length || 0) > 0 ? true : null);
    const [interested, setInterested] = useState(new Set(outingData?.my_interested || []));
    const [dng, setDng] = useState(new Set(outingData?.my_do_not_group || []));
    const [submitting, setSubmitting] = useState(false);
    const { data: parts } = useQuery({ queryKey: ["fb-parts", outingId], queryFn: async () => (await api.get(`/outings/${outingId}/feedback-participants`)).data });

    const toggle = (setFn, set, id) => { const n = new Set(set); n.has(id) ? n.delete(id) : n.add(id); setFn(n); };

    const submit = async () => {
        if (!rating) { toast.error("Pick how it went first."); return; }
        setSubmitting(true);
        try {
            await api.post(`/outings/${outingId}/feedback`, {
                rating, do_again: !!doAgain, attend_another: !!attendAnother,
                interested_in: wantInterest ? [...interested] : [],
                do_not_group: [...dng],
            });
            toast.success("Saved — your feedback and selections stay private.");
            qc.invalidateQueries({ queryKey: ["outing", outingId] });
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
        finally { setSubmitting(false); }
    };

    const YesNo = ({ value, onChange, testid }) => (
        <div className="mt-2 flex gap-2">
            {[["Yes", true], ["No", false]].map(([l, v]) => (
                <button key={l} data-testid={`${testid}-${l.toLowerCase()}`} onClick={() => onChange(v)}
                    className={`rounded-full border px-5 py-2 text-sm font-600 ${value === v ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card"}`}>{l}</button>
            ))}
        </div>
    );

    return (
        <div className="space-y-4 rounded-2xl bg-card p-6 shadow-sm">
            {win && win.open && (
                <div className="rounded-xl bg-accent/10 p-3 text-sm font-600 text-accent" data-testid="interest-countdown">
                    You have {remainingText(win.seconds_remaining)} remaining to update your connection interests.
                </div>
            )}
            {closed && (
                <div className="rounded-xl bg-zinc-100 p-3 text-sm text-zinc-600" data-testid="interest-closed">
                    Your connection window for this activity has closed. Interest selections can no longer be changed.
                </div>
            )}
            <fieldset disabled={locked} className={locked ? "space-y-6 opacity-70" : "space-y-6"}>
            <div>
                <p className="font-display text-lg font-700 text-primary">How did it go?</p>
                <div className="mt-3 flex gap-3">
                    {RATING_FACES.map((f) => (
                        <button key={f.key} data-testid={`fb-rating-${f.key}`} onClick={() => setRating(f.key)}
                            className={`flex flex-col items-center rounded-2xl border px-4 py-3 ${rating === f.key ? "border-accent bg-accent/10" : "border-border"}`}>
                            <span className="text-2xl">{f.emoji}</span><span className="mt-1 text-xs font-600">{f.label}</span>
                        </button>
                    ))}
                </div>
            </div>
            <div><p className="font-600 text-primary">Would you do this activity again?</p><YesNo value={doAgain} onChange={setDoAgain} testid="fb-again" /></div>
            <div><p className="font-600 text-primary">Would you attend another Single Social Club outing?</p><YesNo value={attendAnother} onChange={setAttendAnother} testid="fb-another" /></div>
            <div>
                <p className="font-600 text-primary">Was there anyone you'd be interested in getting to know better?</p>
                <p className="text-xs text-muted-foreground">This is completely private. We only ever act on it if the interest is mutual. Each person's age is shown to help you decide.</p>
                <YesNo value={wantInterest} onChange={setWantInterest} testid="fb-interest" />
                {wantInterest && parts && (
                    <div className="mt-3 flex flex-wrap gap-2">
                        {parts.map((p) => (
                            <button key={p.id} data-testid={`fb-interest-person-${p.id}`} onClick={() => toggle(setInterested, interested, p.id)}
                                className={`rounded-full border px-3.5 py-2 text-sm ${interested.has(p.id) ? "border-accent bg-accent/10 text-accent" : "border-border"}`}>{p.temp_emoji} {p.temp_name}{p.age ? <span className="ml-1 opacity-70">· {p.age}</span> : ""}</button>
                        ))}
                    </div>
                )}
            </div>
            <div>
                <p className="font-600 text-primary">Prefer not to be grouped with anyone from this outing again?</p>
                <p className="text-xs text-muted-foreground">Private. They will never be told.</p>
                {parts && (
                    <div className="mt-3 flex flex-wrap gap-2">
                        {parts.map((p) => (
                            <button key={p.id} data-testid={`fb-dng-person-${p.id}`} onClick={() => toggle(setDng, dng, p.id)}
                                className={`rounded-full border px-3.5 py-2 text-sm ${dng.has(p.id) ? "border-destructive bg-destructive/10 text-destructive" : "border-border"}`}>{p.temp_emoji} {p.temp_name}{p.age ? <span className="ml-1 opacity-70">· {p.age}</span> : ""}</button>
                        ))}
                    </div>
                )}
            </div>
            {!closed && <Button onClick={submit} disabled={submitting || locked} data-testid="fb-submit" className="w-full rounded-full bg-primary hover:bg-primary/90">Save my feedback &amp; selections</Button>}
            </fieldset>
        </div>
    );
}

function ReportDialog({ outingId, participants }) {
    const [category, setCategory] = useState("");
    const [participant, setParticipant] = useState("");
    const [desc, setDesc] = useState("");
    const [open, setOpen] = useState(false);
    const submit = async () => {
        if (!category || !desc) { toast.error("Please add a category and description."); return; }
        try {
            await api.post("/reports", { outing_id: outingId, reported_participant_id: participant || null, category, description: desc });
            toast.success("Report submitted privately. Thank you.");
            setOpen(false); setCategory(""); setDesc(""); setParticipant("");
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    };
    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button variant="ghost" size="sm" data-testid="report-open" className="text-muted-foreground hover:text-destructive"><Flag className="mr-1 h-4 w-4" /> Report a concern</Button>
            </DialogTrigger>
            <DialogContent>
                <DialogHeader><DialogTitle>Report a concern</DialogTitle></DialogHeader>
                <div className="space-y-3">
                    <div>
                        <label className="text-sm font-600">Who? (optional)</label>
                        <Select value={participant} onValueChange={setParticipant}>
                            <SelectTrigger data-testid="report-participant" className="mt-1"><SelectValue placeholder="Select a group member" /></SelectTrigger>
                            <SelectContent>{participants.filter((p) => !p.is_me).map((p, i) => <SelectItem key={i} value={p.id || String(i)}>{p.temp_emoji} {p.temp_name}</SelectItem>)}</SelectContent>
                        </Select>
                    </div>
                    <div>
                        <label className="text-sm font-600">Category</label>
                        <Select value={category} onValueChange={setCategory}>
                            <SelectTrigger data-testid="report-category" className="mt-1"><SelectValue placeholder="Select a category" /></SelectTrigger>
                            <SelectContent>{REPORT_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                        </Select>
                    </div>
                    <div>
                        <label className="text-sm font-600">What happened?</label>
                        <Textarea data-testid="report-desc" value={desc} onChange={(e) => setDesc(e.target.value)} className="mt-1" rows={4} />
                    </div>
                    <p className="text-xs text-muted-foreground">Your report is private. We never reveal who reported to the person being reported.</p>
                </div>
                <DialogFooter><Button onClick={submit} data-testid="report-submit" className="rounded-full bg-destructive hover:bg-destructive/90">Submit report</Button></DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default function OutingDetail() {
    const { id } = useParams();
    const [params] = useSearchParams();
    const nav = useNavigate();
    const qc = useQueryClient();
    const [tab, setTab] = useState(params.get("tab") || "details");
    const { data, error } = useQuery({
        queryKey: ["outing", id],
        queryFn: async () => (await api.get(`/outings/${id}`)).data,
        retry: false,
    });

    if (error) return (
        <div className="py-16 text-center">
            <p className="text-muted-foreground">You don't have access to this outing.</p>
            <Link to="/outings"><Button className="mt-4 rounded-full">Back to my outings</Button></Link>
        </div>
    );
    if (!data) return <PageLoader />;
    const o = data.outing;
    const venue = data.venue;
    const mapsUrl = (q) => `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent((q || "").trim())}`;
    const isPast = o.status === "completed" || (o.date || "") < new Date().toISOString().slice(0, 10);

    const cancel = async () => {
        try { await api.post(`/outings/${id}/cancel`, { reason: null }); toast.success("You've cancelled. Your group has been updated."); qc.invalidateQueries(); nav("/outings"); }
        catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    };
    const voteVenue = async (i) => {
        try { const r = await api.post(`/outings/${id}/vote-venue`, { index: i }); toast.success(r.data.finalized ? "Venue locked in for the group! 📍" : "Your vote is in."); qc.invalidateQueries({ queryKey: ["outing", id] }); }
        catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    };
    const setAttendance = async (attended) => {
        await api.post(`/outings/${id}/attendance`, { attended });
        toast.success("Thanks — attendance recorded.");
        qc.invalidateQueries({ queryKey: ["outing", id] });
    };

    return (
        <div className="space-y-5">
            <button onClick={() => nav("/outings")} className="flex items-center gap-1 text-sm text-accent"><ArrowLeft className="h-4 w-4" /> My Outings</button>
            <div className="rounded-3xl bg-primary p-6 text-primary-foreground">
                <div className="text-4xl">{o.activity_emoji}</div>
                <h1 className="mt-2 font-display text-3xl font-700">{o.activity_name}</h1>
                <div className="mt-3 space-y-1 text-sm text-white/85">
                    <p className="flex items-center gap-2"><Clock className="h-4 w-4" /> {formatDate(o.date)} · {formatTime(o.start_time)} – {formatTime(o.end_time)}</p>
                    {venue && <p className="flex items-center gap-2"><MapPin className="h-4 w-4" /> {venue.name}, {venue.city}</p>}
                    <p className="flex items-center gap-2"><Users className="h-4 w-4" /> {data.participant_count} members · You are {data.my_identity?.temp_emoji} {data.my_identity?.temp_name}</p>
                </div>
            </div>

            <Tabs value={tab} onValueChange={setTab}>
                <TabsList className="w-full">
                    <TabsTrigger value="details" data-testid="tab-details" className="flex-1">Details</TabsTrigger>
                    <TabsTrigger value="chat" data-testid="tab-chat" className="flex-1">Group Chat</TabsTrigger>
                    {isPast && <TabsTrigger value="feedback" data-testid="tab-feedback" className="flex-1">Feedback</TabsTrigger>}
                </TabsList>

                <TabsContent value="details" className="mt-4 space-y-4">
                    {!venue && o.venue_status === "pending_approval" && data.venue_candidates && data.venue_candidates.length > 0 ? (
                        <div className="rounded-2xl bg-card p-5 shadow-sm" data-testid="venue-vote">
                            <p className="font-display text-lg font-700 text-primary">Vote on where to meet</p>
                            <p className="text-xs text-muted-foreground">Your group picks the spot. Most-voted wins — ties go to our top pick.</p>
                            <div className="mt-3 space-y-2">
                                {data.venue_candidates.map((c, i) => {
                                    const mine = data.my_venue_vote === i;
                                    return (
                                        <button key={i} data-testid={`venue-vote-${i}`} onClick={() => voteVenue(i)}
                                            className={`flex w-full items-center justify-between gap-3 rounded-xl border px-4 py-3 text-left transition-colors ${mine ? "border-accent bg-accent/10" : "border-border hover:border-accent/50"}`}>
                                            <div className="min-w-0">
                                                <p className="text-sm font-600 text-primary">{c.name}{i === data.ai_pick_index && <span className="ml-2 rounded bg-primary px-1.5 py-0.5 text-[10px] font-700 text-primary-foreground">Top pick</span>}</p>
                                                <p className="text-xs text-muted-foreground">{c.address}</p>
                                            </div>
                                            <span className="whitespace-nowrap text-xs font-600 text-muted-foreground">{data.venue_vote_counts?.[i] || 0}{mine ? " · your vote" : " votes"}</span>
                                        </button>
                                    );
                                })}
                            </div>
                            <div className="mt-3 flex items-center gap-1.5" data-testid="venue-google-attribution">
                                <img src="/google-maps-logo.png" alt="Google Maps" className="h-4 w-4 object-contain" />
                                <span className="text-[10px] font-600 uppercase tracking-wide text-muted-foreground/70">Venue suggestions powered by Google</span>
                            </div>
                        </div>
                    ) : (!venue && o.venue_status === "pending_approval" && (
                        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-5 text-sm text-amber-800" data-testid="venue-finalizing">
                            📍 <span className="font-700">Venue being finalized.</span> Our team is confirming the best spot for your group — you'll get a notification the moment it's set.
                        </div>
                    ))}
                    {venue && (
                        <div className="rounded-2xl bg-card p-5 shadow-sm">
                            <p className="text-xs font-700 uppercase tracking-wide text-muted-foreground">Venue</p>
                            <p className="mt-1 font-display text-lg font-700 text-primary">{venue.name}</p>
                            <p className="text-sm text-muted-foreground">{venue.street}, {venue.city}, {venue.state} {venue.zip_code}</p>
                            <a href={mapsUrl(`${venue.name} ${venue.street || ""} ${venue.city || ""} ${venue.state || ""} ${venue.zip_code || ""}`)} target="_blank" rel="noreferrer"
                                data-testid="venue-open-maps" className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-secondary px-3.5 py-1.5 text-sm font-600 text-primary transition-colors hover:bg-secondary/70">
                                <MapPin className="h-4 w-4" /> Open in Google Maps
                            </a>
                            <div className="mt-3 flex items-center gap-1.5" data-testid="venue-google-attribution">
                                <img src="/google-maps-logo.png" alt="Google Maps" className="h-4 w-4 object-contain" />
                                <span className="text-[10px] font-600 uppercase tracking-wide text-muted-foreground/70">Venue details powered by Google</span>
                            </div>
                        </div>
                    )}
                    <div className="rounded-2xl bg-card p-5 shadow-sm">
                        <p className="text-xs font-700 uppercase tracking-wide text-muted-foreground">Your group</p>
                        <div className="mt-3 flex flex-wrap gap-2">
                            {data.participants.map((p, i) => (
                                <span key={i} className={`rounded-full px-3 py-1.5 text-sm font-600 ${p.is_me ? "bg-primary text-primary-foreground" : "bg-secondary text-foreground"}`}>{p.temp_emoji} {p.temp_name}{p.is_me ? " (you)" : ""}</span>
                            ))}
                        </div>
                    </div>
                    <div className="flex items-start gap-3 rounded-2xl bg-secondary/60 p-4 text-sm text-muted-foreground">
                        <ShieldCheck className="mt-0.5 h-5 w-5 flex-none text-primary" />
                        <p>Meet the group at the venue. Everyone arrives independently. Keep it friendly and respectful — and use the group chat rather than sharing personal contact info at your first meeting.</p>
                    </div>
                    {isPast && (
                        <div className="rounded-2xl bg-card p-5 shadow-sm">
                            <p className="font-600 text-primary">Did you attend?</p>
                            <div className="mt-2 flex gap-2">
                                <Button onClick={() => setAttendance(true)} data-testid="attend-yes" variant={data.my_attendance === "attended" ? "default" : "outline"} className="rounded-full">{data.my_attendance === "attended" && <Check className="mr-1 h-4 w-4" />} Yes</Button>
                                <Button onClick={() => setAttendance(false)} data-testid="attend-no" variant={data.my_attendance === "no_show" ? "default" : "outline"} className="rounded-full">No</Button>
                            </div>
                        </div>
                    )}
                    <div className="flex items-center justify-between">
                        <ReportDialog outingId={id} participants={data.participants} />
                        {!isPast && (
                            <Button onClick={cancel} variant="ghost" size="sm" data-testid="cancel-outing" className="text-muted-foreground hover:text-destructive"><XCircle className="mr-1 h-4 w-4" /> Cancel my spot</Button>
                        )}
                    </div>
                </TabsContent>

                <TabsContent value="chat" className="mt-4"><Chat outingId={id} /></TabsContent>

                {isPast && (
                    <TabsContent value="feedback" className="mt-4">
                        <FeedbackFlow outingId={id} outingData={data} onDone={() => setTab("details")} />
                    </TabsContent>
                )}
            </Tabs>
        </div>
    );
}
